'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface ContributionCardProps {
  title: string;
  description: string;
  icon: string;
  suggestedAmounts: number[];
  onContribute: (category: string, amount: number) => void;
  category: string;
}

const ContributionCard: React.FC<ContributionCardProps> = ({
  title,
  description,
  icon,
  suggestedAmounts,
  onContribute,
  category,
}) => {
  const [selectedAmount, setSelectedAmount] = React.useState<number | null>(null);
  const [customAmount, setCustomAmount] = React.useState<string>('');

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setCustomAmount(value);
    setSelectedAmount(null);
  };

  const handleContribute = () => {
    const amount = selectedAmount || parseFloat(customAmount);
    if (amount && amount > 0) {
      onContribute(category, amount);
    }
  };

  const isValid = selectedAmount || (customAmount && parseFloat(customAmount) > 0);

  return (
    <div className="bg-card rounded-2xl shadow-sacred-md p-8 border border-border hover:shadow-sacred-lg transition-all duration-300">
      <div className="flex items-start space-x-4 mb-6">
        <div className="flex-shrink-0 w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
          <Icon name={icon as any} size={26} className="text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-playfair text-xl lg:text-2xl font-semibold text-foreground mb-3">
            {title}
          </h3>
          <p className="font-inter text-sm text-muted-foreground leading-relaxed">
            {description}
          </p>
        </div>
      </div>

      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          {suggestedAmounts.map((amount) => (
            <button
              key={amount}
              onClick={() => handleAmountSelect(amount)}
              className={`px-4 py-3.5 rounded-xl font-inter text-sm font-medium transition-all duration-200 ${
                selectedAmount === amount
                  ? 'bg-primary text-primary-foreground shadow-md scale-105'
                  : 'bg-surface text-foreground hover:bg-primary/10 border border-border'
              }`}
            >
              ${amount.toLocaleString('en-US')}
            </button>
          ))}
        </div>

        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-inter text-sm">
            $
          </span>
          <input
            type="text"
            value={customAmount}
            onChange={handleCustomAmountChange}
            placeholder="Custom amount"
            className="w-full pl-8 pr-4 py-3.5 bg-input border border-border rounded-xl font-inter text-sm text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200"
          />
        </div>

        <button
          onClick={handleContribute}
          disabled={!isValid}
          className={`w-full py-3.5 rounded-xl font-inter text-sm font-medium transition-all duration-200 ${
            isValid
              ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-md hover:shadow-lg hover:scale-[1.02]'
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          }`}
        >
          Contribute to {title}
        </button>
      </div>
    </div>
  );
};

export default ContributionCard;